import { useEffect, useMemo, useRef, useState } from "react";

import {
  buildNarrationDebugReport,
  logDirectorViewEvent,
  logNarrationResolution,
} from "./directorViewDebug";
import {
  buildNarrationDrift,
  type NarrationDriftReport,
} from "./narrationDrift";
import {
  buildNarrationTimeline,
  isNarrationTimeline,
  narrationIndexAtTime,
  type NarrationAbsentReason,
  type NarrationLine,
  type NarrationTimeline,
} from "./narrationTimeline";
import type { SyncTimeline } from "./syncTimeline";
import {
  useAudioDurations,
  useAudioPeaks,
  type AudioPeaksState,
} from "./useAudioPeaks";
import { signMediaSource, type MediaUrlSource } from "./useMediaUrl";

// ---------------------------------------------------------------------------
// useDirectorNarration — one resolution of everything narration-related, shared
// by every surface that shows it (DIR-013).
//
// The inline panel, the fullscreen panel and the fullscreen captions all need
// the same narration timeline, the same drift report and the same "which line
// is being spoken right now". Resolving that per surface would measure the same
// clips twice and, worse, decode the same 20 MB MP4 twice. So the Echo view
// owns a single instance of this hook and hands the result to whichever surface
// is on screen.
//
// The two cost switches are separate on purpose: `enabled` covers the cheap
// work (pure resolution, clip-length probes, tracking the active line), while
// `withPeaks` gates the expensive whole-file download and decode that only the
// waveform lane needs. Captions can therefore be on with no audio download.
// ---------------------------------------------------------------------------

/** Same boundary hysteresis the scene driver uses, for the same reason. */
const BOUNDARY_HYSTERESIS_SECONDS = 0.12;

export type DirectorNarrationState = {
  narration: NarrationTimeline | null;
  absentReason: NarrationAbsentReason | null;
  drift: NarrationDriftReport | null;
  peaks: AudioPeaksState;
  /** Index of the line under the playhead, or -1 between lines. */
  activeLineIndex: number;
  activeLine: NarrationLine | null;
  beats: number[];
  cutTimes: number[];
};

export type DirectorNarrationInputs = {
  envelope: Record<string, unknown> | undefined;
  manifestBody: unknown;
  sync: SyncTimeline | null;
  /** The player currently driving playback (inline or fullscreen). */
  video: HTMLVideoElement | null;
  finalVideoSource: MediaUrlSource | null;
  /** Bucket the per-line narration clips live in (same as the manifest's). */
  mediaBucket: string | null;
  workloadId?: string | null;
  finalVideoStoragePath?: string | null;
  /** Cheap work: resolve, measure clip lengths, follow the playhead. */
  enabled: boolean;
  /** Expensive work: download and decode the delivered audio for the waveform. */
  withPeaks: boolean;
};

/**
 * Track the narration line under the playhead. Mirrors the scene driver: the
 * lookup runs outside React and only a real change pushes state, so a playing
 * video does not re-render every frame.
 */
function useActiveLine(
  video: HTMLVideoElement | null,
  lines: NarrationLine[],
  enabled: boolean,
): number {
  const [activeIndex, setActiveIndex] = useState(-1);
  const displayedRef = useRef(-2);

  useEffect(() => {
    displayedRef.current = -2;
  }, [lines]);

  useEffect(() => {
    if (!video || !enabled || lines.length === 0) {
      return;
    }

    const evaluate = (mediaTime?: number) => {
      const seconds = mediaTime ?? video.currentTime;
      if (!Number.isFinite(seconds)) return;
      const position = narrationIndexAtTime(lines, seconds);
      const next = position >= 0 ? lines[position].index : -1;
      if (next === displayedRef.current) return;
      // The field desync verifier: what the tracker believes is being spoken,
      // against the video clock, at the moment it changes its mind. If what
      // you HEAR disagrees with this window, the resolution is wrong; if they
      // agree but the picture is wrong, the render is. Verbose-gated — it
      // fires once per line, not per frame.
      if (position >= 0) {
        const line = lines[position];
        logDirectorViewEvent(
          "line",
          {
            index: line.index,
            at: Math.round(seconds * 1000) / 1000,
            window: [line.startSeconds, line.endSeconds],
            text: line.text.slice(0, 60),
          },
          { verboseOnly: true },
        );
      }
      // Leaving a line right at its edge is jitter; wait until we are clear.
      if (next === -1 && displayedRef.current >= 0) {
        const previous = lines.find(
          (line) => line.index === displayedRef.current,
        );
        if (
          previous &&
          seconds >= previous.endSeconds &&
          seconds - previous.endSeconds < BOUNDARY_HYSTERESIS_SECONDS
        ) {
          return;
        }
      }
      displayedRef.current = next;
      setActiveIndex(next);
    };

    const onTimeUpdate = () => evaluate();
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("seeked", onTimeUpdate);

    let handle: number | null = null;
    const supportsRvfc = "requestVideoFrameCallback" in video;
    if (supportsRvfc) {
      const onFrame = (
        _now: DOMHighResTimeStamp,
        metadata: VideoFrameCallbackMetadata,
      ) => {
        evaluate(metadata.mediaTime);
        handle = video.requestVideoFrameCallback(onFrame);
      };
      handle = video.requestVideoFrameCallback(onFrame);
    }
    evaluate();

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("seeked", onTimeUpdate);
      if (supportsRvfc && handle != null) {
        video.cancelVideoFrameCallback(handle);
      }
    };
  }, [video, lines, enabled]);

  // A disabled surface must not keep reporting a stale active line.
  return enabled ? activeIndex : -1;
}

export function useDirectorNarration({
  envelope,
  manifestBody,
  sync,
  video,
  finalVideoSource,
  mediaBucket,
  workloadId,
  finalVideoStoragePath,
  enabled,
  withPeaks,
}: DirectorNarrationInputs): DirectorNarrationState {
  // Resolve once without measurements so we know which clips are worth probing.
  const baseResult = useMemo(
    () =>
      buildNarrationTimeline({
        envelope,
        manifest: manifestBody,
        sync,
        sessionWorkloadId: workloadId ?? null,
        finalVideoStoragePath: finalVideoStoragePath ?? null,
      }),
    [envelope, manifestBody, sync, workloadId, finalVideoStoragePath],
  );

  const baseTimeline = isNarrationTimeline(baseResult) ? baseResult : null;
  // Measuring only helps the tiers that would otherwise guess a line's length.
  const wantsMeasurement =
    enabled &&
    baseTimeline != null &&
    (baseTimeline.source === "planned_estimate" ||
      baseTimeline.source === "voiceover_files");

  const durationSources = useMemo(() => {
    if (!wantsMeasurement || !mediaBucket || !baseTimeline) return [];
    return baseTimeline.lines
      .filter((line) => line.audioKey != null)
      .map((line) => ({
        index: line.index,
        source: {
          storage_bucket: mediaBucket,
          storage_path: line.audioKey,
          storage_is_public: false,
        } satisfies MediaUrlSource,
      }));
  }, [wantsMeasurement, mediaBucket, baseTimeline]);

  const { durations } = useAudioDurations(durationSources, {
    enabled: wantsMeasurement && durationSources.length > 0,
    signUrl: signMediaSource,
  });

  const result = useMemo(
    () =>
      buildNarrationTimeline({
        envelope,
        manifest: manifestBody,
        sync,
        measuredDurations: durations.size > 0 ? durations : null,
        sessionWorkloadId: workloadId ?? null,
        finalVideoStoragePath: finalVideoStoragePath ?? null,
      }),
    [
      envelope,
      manifestBody,
      sync,
      durations,
      workloadId,
      finalVideoStoragePath,
    ],
  );

  const narration = isNarrationTimeline(result) ? result : null;
  const absentReason =
    !isNarrationTimeline(result) && result ? result.reason : null;

  const drift = useMemo(
    () => (narration && sync ? buildNarrationDrift(narration, sync) : null),
    [narration, sync],
  );

  // One log line per resolution — including the re-resolution after clip
  // measurement lands, so a tier promotion is visible as two lines. This is
  // the "is it working in production" receipt an operator can copy.
  useEffect(() => {
    if (!enabled || (!narration && !absentReason)) return;
    logNarrationResolution(
      buildNarrationDebugReport({
        workloadId,
        sync,
        narration,
        absentReason,
        drift,
      }),
    );
  }, [enabled, workloadId, sync, narration, absentReason, drift]);

  const peaks = useAudioPeaks(finalVideoSource, { enabled: withPeaks });

  const lines = useMemo(() => narration?.lines ?? [], [narration]);
  const activeLineIndex = useActiveLine(video, lines, enabled);
  const activeLine =
    lines.find((line) => line.index === activeLineIndex) ?? null;

  const beatData = useMemo(() => {
    const manifest =
      manifestBody && typeof manifestBody === "object"
        ? (manifestBody as Record<string, unknown>)
        : null;
    const beatSync =
      manifest && typeof manifest.beat_sync === "object" && manifest.beat_sync
        ? (manifest.beat_sync as Record<string, unknown>)
        : null;
    const asNumbers = (value: unknown): number[] =>
      Array.isArray(value)
        ? value.filter(
            (entry): entry is number =>
              typeof entry === "number" && Number.isFinite(entry),
          )
        : [];
    return {
      beats: asNumbers(beatSync?.beats),
      cuts: asNumbers(beatSync?.cut_times),
    };
  }, [manifestBody]);

  return {
    narration,
    absentReason,
    drift,
    peaks,
    activeLineIndex,
    activeLine,
    beats: beatData.beats,
    cutTimes: beatData.cuts,
  };
}
